import React, { useEffect, useMemo, useRef, useState } from "react";
import { CustomSelect, TextInput, Tooltip } from "../..";
import { Search } from "lucide-react";
import Denmark from "@/assets/icons/svg/denmark_fav.svg";
import { isSectionAlreadySelected } from "./priorityUtils";
import { SectionEntity } from "@/database";
import FavSectionChips from "./FavSectionChips";

export type MultiPublishTarget = {
  section: number;
  parent_id: number;
  channel_name?: string;
  host?: number;
  folder_path?: string;
  custom_title?: string;
  custom_caption?: string;
  open_story_at?: "PUBLISH_SECTION" | "MULTI_PUBLISH_SECTION";
  open_newly_in_browser?: boolean;
  isOverRideLinkRequired?: boolean;
  contentPublishType: "MULTIPUBLISH";
  multiPublishSync?: boolean;
};

type Props = {
  currentArticle: any;
  selectedChip?: number;
  selectedHost: string;
  disabled: boolean;
  hostOptions: any[];
  hosts: any[];
  denmarkHosts?: any[];
  sectionOptions: any[];
  globalFavSectionList?: SectionEntity[];
  customFetchHostSection: (params: any) => void;
  handleHostChangeFromSelect: (host: any) => void;
  customOptionRenderer: (option: any) => React.ReactNode;
  hostOptionRenderer: (option: any) => React.ReactNode;
  hostValueRenderer: (option: any) => React.ReactNode;
  updateSelectedArticleByChip: (updates: Record<string, any>) => void;
  scrollToSection?: number;
  selectedSections?: Record<string, unknown>[];
  selectedArticle?: Record<string, unknown>[];
  activeChip?: number | null;
  /** Media-form draft rows (e.g. child chips with `_parentSectionId`); when a removed target’s section is listed here, form state is synced. */
  currentSelectedSection?: {
    section?: number;
    job_type?: string;
    _parentSectionId?: number;
  }[];
  onSyncFormStateForRemovedTarget?: (removedTargetSectionId: number) => void;
};

const DenmarkMultiPublishTargets: React.FC<Props> = ({
  currentArticle,
  selectedChip,
  selectedHost,
  disabled,
  hostOptions,
  hosts,
  denmarkHosts = [],
  sectionOptions,
  globalFavSectionList = [],
  customFetchHostSection,
  handleHostChangeFromSelect,
  customOptionRenderer,
  hostOptionRenderer,
  hostValueRenderer,
  updateSelectedArticleByChip,
  scrollToSection,
  selectedSections = [],
  selectedArticle = [],
  activeChip = null,
  currentSelectedSection = [],
  onSyncFormStateForRemovedTarget,
}) => {
  const rowRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const multiPublishTargets: MultiPublishTarget[] = useMemo(() => {
    const raw = currentArticle?.multi_publish_targets;
    return Array.isArray(raw) ? (raw as MultiPublishTarget[]) : [];
  }, [currentArticle]);

  const publishedTargetSections = useMemo(
    () =>
      new Set(
        selectedSections.flatMap((s) =>
          Array.isArray(s.multi_publish_targets)
            ? (s.multi_publish_targets as { section?: number }[]).map((pt) =>
                Number(pt.section)
              )
            : []
        )
      ),
    [selectedSections]
  );

  const [expandedTargetSection, setExpandedTargetSection] = useState<
    number | null
  >(null);

  const [multiPublishSync, setMultiPublishSync] = useState(false);

  useEffect(() => {
    setExpandedTargetSection(null);
    setMultiPublishSync(false);
    // Always stamp false on all targets when chip changes so the toggle and
    // targets are in sync even if the user never touches the toggle.
    if (multiPublishTargets.length > 0) {
      const reset = multiPublishTargets.map((t) => ({
        ...t,
        multiPublishSync: false,
      }));
      updateSelectedArticleByChip({ multi_publish_targets: reset });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChip]);

  useEffect(() => {
    if (!scrollToSection) return;
    setExpandedTargetSection(scrollToSection);
    const el = rowRefs.current[scrollToSection];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [scrollToSection]);

  const handleMultiPublishSyncToggle = () => {
    const next = !multiPublishSync;
    setMultiPublishSync(next);
    // Stamp toggle value only on targets that were NOT individually edited.
    // Individually-edited targets (multiPublishSync === true via updateMultiPublishTarget)
    // keep their own true value regardless of the toggle.
    const updatedTargets = multiPublishTargets.map((t) =>
      t.multiPublishSync === true ? t : { ...t, multiPublishSync: next }
    );
    updateSelectedArticleByChip({ multi_publish_targets: updatedTargets });
  };

  const addMultiPublishTargetFromOption = (option: any) => {
    if (!option) return;

    const sectionId = Number(option.value ?? option.section ?? option.id);
    if (!sectionId || Number.isNaN(sectionId)) return;

    const sectionAsRecord = { section: sectionId, value: sectionId } as Record<
      string,
      unknown
    >;
    const articleWithTargets: Record<string, unknown>[] = [
      ...selectedArticle,
      ...(multiPublishTargets as unknown as Record<string, unknown>[]),
    ];
    if (
      isSectionAlreadySelected(
        sectionAsRecord,
        selectedSections,
        articleWithTargets,
        activeChip
      )
    )
      return;

    const next: MultiPublishTarget[] = [
      ...multiPublishTargets,
      {
        section: sectionId,
        parent_id: sectionId,
        channel_name: option.title ?? option.label ?? "",
        host: option.host != null ? Number(option.host) : Number(selectedHost),
        folder_path: option.path ?? option.folder_path ?? "",
        open_story_at: "PUBLISH_SECTION",
        open_newly_in_browser: false,
        isOverRideLinkRequired: true,
        contentPublishType: "MULTIPUBLISH",
        multiPublishSync,
      },
    ];
    updateSelectedArticleByChip({ multi_publish_targets: next });
  };

  const updateMultiPublishTarget = (
    sectionId: number,
    updates: Partial<MultiPublishTarget>
  ) => {
    // Any individual edit on a target marks it as multiPublishSync: true,
    // independent of the global toggle.
    const next = multiPublishTargets.map((t) =>
      Number(t.section) === Number(sectionId)
        ? { ...t, ...updates, multiPublishSync: true }
        : t
    );
    updateSelectedArticleByChip({ multi_publish_targets: next });
  };

  const removeMultiPublishTarget = (sectionId: number) => {
    const sid = Number(sectionId);
    const next = multiPublishTargets.filter((t) => Number(t.section) !== sid);
    updateSelectedArticleByChip({ multi_publish_targets: next });
    const hasFormDraft = (currentSelectedSection || []).some(
      (item) => Number(item?.section) === sid
    );
    if (hasFormDraft && onSyncFormStateForRemovedTarget) {
      onSyncFormStateForRemovedTarget(sid);
    }
    setExpandedTargetSection((prev) =>
      Number(prev) === Number(sectionId) ? null : prev
    );
  };

  return (
    <div className="mb-4">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h6 className="mb-0 text-uppercase fw-semibold ">
          <i className="bi bi-diagram-2 fs-4"></i> Multi-Publish Targets
        </h6>
        {globalFavSectionList.length > 0 && (
          <FavSectionChips
            globalFavSectionList={globalFavSectionList}
            hosts={hosts}
            hostOptions={hostOptions}
            denmarkHosts={denmarkHosts}
            selectedHost={selectedHost}
            onSelect={(section) =>
              addMultiPublishTargetFromOption({
                value: section.msId,
                title: section.name ?? "",
                path: section.path ?? "",
              })
            }
          />
        )}
        {/* <div className="d-flex align-items-center gap-2 justify-content-center">
          <span className="small fw-semibold text-muted">
            Multi Publish Sync
          </span>
          <div className="form-check form-switch mt-2">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="multiPublishSyncToggle"
              checked={multiPublishSync}
              disabled={disabled}
              onChange={handleMultiPublishSyncToggle}
              style={{ cursor: disabled ? "not-allowed" : "pointer" }}
            />
          </div>
        </div> */}
      </div>

      <div className="d-flex align-items-center position-relative">
        <div className="position-relative w-100">
          <Search
            className="position-absolute text-secondary"
            size={16}
            style={{
              top: "50%",
              left: "12px",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              zIndex: 2,
            }}
          />
          <CustomSelect
            className="w-100"
            name="multiPublishTargets"
            label=""
            value={[]}
            handleChange={(option: any) => {
              addMultiPublishTargetFromOption(option);
            }}
            options={sectionOptions}
            disabled={disabled || !hostOptions?.length}
            hostSubSection={true}
            onInputChangeCallback={customFetchHostSection}
            callbackParams={selectedHost}
            customOptionRenderer={customOptionRenderer}
            updatedOptions={sectionOptions}
            hideArrow={true}
            customStyling={{
              padding: "0 40px 0 32px",
            }}
            placeholder="Search section to add as multi-publish target..."
            loader={true}
            disableMenuPortal={false}
          />
        </div>

        {!hosts || hosts.length === 0 ? (
          <Tooltip content="Request Denmark access">
            <div
              className="position-absolute end-0 bg-light cursor-pointer d-flex align-items-center justify-content-center border"
              style={{
                width: "40px",
                height: "40px",
                right: 0,
              }}
            >
              <img
                src={Denmark}
                alt="Denmark"
                style={{
                  width: "18px",
                  height: "18px",
                  opacity: 0.5,
                  filter: "grayscale(100%)",
                }}
              />
            </div>
          </Tooltip>
        ) : (
          <CustomSelect
            className="position-absolute end-0 cursor-pointer"
            name="selectedHost"
            value={(() => {
              const matched = hostOptions?.find(
                (option: any) => String(option.value) === String(selectedHost)
              );
              return matched ? matched.value : hostOptions?.[0]?.value || "";
            })()}
            handleChange={handleHostChangeFromSelect}
            options={hosts && hosts.length > 0 ? hostOptions : []}
            ishost={true}
            hideArrow={true}
            disabled={true}
            customOptionRenderer={hostOptionRenderer}
            customValueRenderer={hostValueRenderer}
            lowOpacity={false}
            menuWidth="200px"
            menuAlign="extend-right"
            isMenuWidthFixed={true}
          />
        )}
      </div>

      {multiPublishTargets.length > 0 && (
        <div className="mt-3">
          {multiPublishTargets.map((t) => {
            const isExpanded =
              expandedTargetSection != null &&
              Number(expandedTargetSection) === Number(t.section);
            const isTargetPublished =
              currentArticle?.status === 10 &&
              publishedTargetSections.has(Number(t.section));
            return (
              <div
                key={`multi-${t.section}`}
                ref={(el) => {
                  rowRefs.current[t.section] = el;
                }}
                className="border rounded-3 p-2 mb-2"
                style={{
                  background: isExpanded ? "#f8fafc" : "#fff",
                  cursor: disabled ? "default" : "pointer",
                }}
                onClick={() => {
                  if (disabled) return;
                  setExpandedTargetSection((prev) =>
                    Number(prev) === Number(t.section)
                      ? null
                      : Number(t.section)
                  );
                }}
              >
                <div className="d-flex align-items-center gap-2">
                  <div className="flex-grow-1 text-truncate">
                    <div className="fw-semibold text-truncate">
                      {t.channel_name || `Section ${t.section}`}
                    </div>
                    <div className="text-muted small text-truncate">
                      {t.folder_path || ""}
                    </div>
                  </div>
                  {/* <button
                    type="button"
                    className="btn btn-sm btn-outline-primary fw-semibold rounded-4"
                    onClick={() => {
                      if (disabled) return;
                      setExpandedTargetSection((prev) =>
                        Number(prev) === Number(t.section)
                          ? null
                          : Number(t.section)
                      );
                    }}
                    disabled={disabled}
                    style={{ padding: "6px 14px" }}
                  >
                    Customize
                  </button> */}
                  {!disabled && !isTargetPublished && (
                    <i
                      className="bi bi-x-lg flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeMultiPublishTarget(t.section);
                      }}
                      style={{
                        cursor: "pointer",
                        fontSize: 13,
                        color: "#dc3545",
                      }}
                    />
                  )}
                </div>

                {isExpanded && (
                  <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                    <div className="mb-3">
                      <TextInput
                        label="Title"
                        name={`custom_title_${t.section}`}
                        value={
                          t.custom_title ??
                          currentArticle?.title ??
                          currentArticle?.mediaTitle ??
                          ""
                        }
                        onChange={(e) =>
                          updateMultiPublishTarget(t.section, {
                            custom_title: e.target.value,
                          })
                        }
                        placeholder="Enter custom title..."
                        disabled={disabled}
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label text-uppercase small fw-semibold">
                        Description
                      </label>
                      <textarea
                        className="form-control"
                        placeholder="Add an optional caption..."
                        value={
                          t.custom_caption ??
                          currentArticle?.desc ??
                          currentArticle?.description ??
                          ""
                        }
                        rows={3}
                        disabled={disabled}
                        onChange={(e) =>
                          updateMultiPublishTarget(t.section, {
                            custom_caption: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                      <div>
                        <div className="text-uppercase small fw-semibold mb-2">
                          Open story at
                        </div>
                        <div className="d-flex align-items-center gap-3">
                          <label className="d-flex align-items-center gap-2">
                            <input
                              type="radio"
                              name={`open-story-${t.section}`}
                              checked={
                                (t.open_story_at ?? "PUBLISH_SECTION") ===
                                "MULTI_PUBLISH_SECTION"
                              }
                              disabled={disabled}
                              onChange={() =>
                                updateMultiPublishTarget(t.section, {
                                  open_story_at: "MULTI_PUBLISH_SECTION",
                                  isOverRideLinkRequired: false,
                                })
                              }
                            />
                            <span className="text-uppercase small fw-semibold">
                              Multi-Publish Section
                            </span>
                          </label>
                          <label className="d-flex align-items-center gap-2">
                            <input
                              type="radio"
                              name={`open-story-${t.section}`}
                              checked={
                                (t.open_story_at ?? "PUBLISH_SECTION") ===
                                "PUBLISH_SECTION"
                              }
                              disabled={disabled}
                              onChange={() =>
                                updateMultiPublishTarget(t.section, {
                                  open_story_at: "PUBLISH_SECTION",
                                  isOverRideLinkRequired: true,
                                })
                              }
                            />
                            <span className="text-uppercase small fw-semibold">
                              Publish Section
                            </span>
                          </label>
                        </div>
                      </div>

                      <label className="d-flex align-items-center gap-2">
                        <input
                          type="checkbox"
                          checked={Boolean(t.open_newly_in_browser)}
                          disabled={disabled}
                          onChange={(e) =>
                            updateMultiPublishTarget(t.section, {
                              open_newly_in_browser: e.target.checked,
                            })
                          }
                        />
                        <span className="text-uppercase small fw-semibold">
                          Open newly in browser
                        </span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DenmarkMultiPublishTargets;
